import React from 'react';
import { G, Line, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** T_MA — AHU casing with OA + RA dampers, color-tinted streams, mixed air leaving. */
export function DiagramMixedAirTemperature() {
  // AHU casing
  const ax = 110, ay = 50, aw = 130, ah = 165;

  return (
    <DiagramCanvas>
      {/* AHU outline */}
      <Rect x={ax} y={ay} width={aw} height={ah} fill={DT.fill} stroke={DT.stroke} strokeWidth="2" rx="4" />
      <SvgText x={ax + aw / 2} y={ay - 4} fill={DT.desc} fontSize="9" fontFamily={DT.mono} textAnchor="middle">AHU mixing box</SvgText>

      {/* OA stream — cool tinted (top) */}
      <Rect x={20} y={ay + 12} width={ax - 20} height={28} fill="rgba(122,170,187,0.22)" stroke={DT.stroke} strokeWidth="1.5" />
      {/* OA damper blades */}
      {[0, 1, 2, 3].map(i => (
        <Line key={i} x1={ax - 32 + i * 6} y1={ay + 12} x2={ax - 26 + i * 6} y2={ay + 40}
          stroke={DT.varColor} strokeWidth="1.4" />
      ))}
      <Line x1={28} y1={ay + 26} x2={ax - 6} y2={ay + 26} stroke={DT.arrow} strokeWidth="1.6" />
      <Polygon points={`${ax - 4},${ay + 26} ${ax - 10},${ay + 22} ${ax - 10},${ay + 30}`} fill={DT.arrow} />
      {/* T_OA badge */}
      <Rect x={28} y={ay + 1} width={48} height={11} fill="rgba(122,170,187,0.85)" rx="2" />
      <SvgText x={52} y={ay + 10} fill="#0D1B2A" fontSize="8.5" fontFamily={DT.mono} fontWeight="700" textAnchor="middle">T_OA cool</SvgText>

      {/* RA stream — warm tinted (bottom) */}
      <Rect x={20} y={ay + ah - 40} width={ax - 20} height={28} fill="rgba(255,107,26,0.22)" stroke={DT.stroke} strokeWidth="1.5" />
      {[0, 1, 2, 3].map(i => (
        <Line key={i} x1={ax - 32 + i * 6} y1={ay + ah - 12} x2={ax - 26 + i * 6} y2={ay + ah - 40}
          stroke={DT.varColor} strokeWidth="1.4" />
      ))}
      <Line x1={28} y1={ay + ah - 26} x2={ax - 6} y2={ay + ah - 26} stroke={DT.arrow} strokeWidth="1.6" />
      <Polygon points={`${ax - 4},${ay + ah - 26} ${ax - 10},${ay + ah - 30} ${ax - 10},${ay + ah - 22}`} fill={DT.arrow} />
      {/* T_RA badge */}
      <Rect x={28} y={ay + ah - 11} width={48} height={11} fill="rgba(255,107,26,0.85)" rx="2" />
      <SvgText x={52} y={ay + ah - 3} fill="#0D1B2A" fontSize="8.5" fontFamily={DT.mono} fontWeight="700" textAnchor="middle">T_RA warm</SvgText>

      {/* Mixing zone — gradient stripes */}
      {Array.from({ length: 7 }).map((_, i) => {
        const yp = ay + 50 + i * 14;
        return (
          <Line key={i} x1={ax + 18} y1={yp} x2={ax + aw - 18} y2={yp}
            stroke={i < 4 ? '#7AAABB' : '#FF6B1A'} strokeWidth="1.2" opacity={0.35} />
        );
      })}
      {/* Turbulence eddies */}
      <Polygon points={`${ax + 30},${ay + ah / 2 - 16} ${ax + 50},${ay + ah / 2} ${ax + 30},${ay + ah / 2 + 16} ${ax + 50},${ay + ah / 2 + 32}`}
        fill="none" stroke={DT.leader} strokeWidth="1" opacity={0.5} />

      {/* Mixed air leaving (right) */}
      <Rect x={ax + aw} y={ay + ah / 2 - 16} width={330 - (ax + aw)} height={32}
        fill="rgba(230,180,140,0.18)" stroke={DT.stroke} strokeWidth="1.5" />
      <Line x1={ax + aw + 8} y1={ay + ah / 2} x2={325} y2={ay + ah / 2} stroke={DT.arrow} strokeWidth="1.6" />
      <Polygon points={`325,${ay + ah / 2} 319,${ay + ah / 2 - 4} 319,${ay + ah / 2 + 4}`} fill={DT.arrow} />
      <Rect x={ax + aw + 14} y={ay + ah / 2 - 30} width={56} height={11} fill="rgba(230,180,140,0.85)" rx="2" />
      <SvgText x={ax + aw + 42} y={ay + ah / 2 - 22} fill="#0D1B2A" fontSize="8.5" fontFamily={DT.mono} fontWeight="700" textAnchor="middle">T_MA</SvgText>

      {/* Labels */}
      <LeaderLine x1={70} y1={ay + 26} x2={20} y2={210} varName="Q_OA · T_OA" desc="OA stream" anchor="start" />
      <LeaderLine x1={70} y1={ay + ah - 26} x2={150} y2={210} varName="Q_RA · T_RA" desc="RA stream" anchor="middle" />
      <LeaderLine x1={290} y1={ay + ah / 2} x2={300} y2={210} varName="T_MA" desc="Mixed (°F)" anchor="end" />

      <FormulaTag formula="T_MA = (Q_OA·T_OA + Q_RA·T_RA) / (Q_OA + Q_RA)" />
    </DiagramCanvas>
  );
}
